<template>
  <article>
    <activity-stage :customRootContainerClass="'words'">
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <div class="words-container">
          <assessment-helper></assessment-helper>

          <section>
            <blinking-cursor-text
              v-for="(word, index) in currentRound.words"
              :key="`step${index}`"
              :text="word"
              :text-step="index"
            ></blinking-cursor-text>
          </section>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';

import ActivityManager from 'mixins/ActivityManager';
import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';

import ActivityStage from 'components/activity/ActivityStage.vue';
import AssessmentHelper from 'components/utils/AssessmentHelper.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';

export default {
  components: {
    ActivityStage,
    AssessmentHelper,
    BlinkingCursorText
  },

  mixins: [ActivityManager, AssetLoader, ErrorLogger, TypingActivityHelper],

  data() {
    return {
      activityName: 'Words'
    };
  },

  methods: {
    async loadActivity() {
      this.createRounds({ stepIndicator: 'words' });
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard || {
        keyboardType: undefined,
        roofs: undefined
      };

      return {
        keyboardType,
        roofs
      };
    },

    startActivity() {
      this.setRound();
      this.errorLoggerStart();
      this.$nextTick(() => {
        this.setActivityContainerHeight();
      });
    },

    setRound() {
      this.setCurrentStep({ stepCallback: this.stepCallback });
      this.setupNewRound({ stepCallback: this.stepCallback });
      this.enableKeyboard();
      this.$nextTick(() => {
        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
      });
    },

    stepCallback() {
      return this.currentStep;
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        if (this.progress.stepIndex === this.totalStepsInRound - 1) {
          this.handleRoundEnd();
          return;
        }
        this.setCurrentStep({ stepCallback: this.stepCallback });
      }
    },

    handleRoundEnd() {
      const t = new TimelineLite();
      this.sndBell();

      t.delay(0.8).call(() => {
        if (this.isLastRound) {
          this.handleActivityEnd();
        } else {
          t.delay(1)
            .to('.activity-stage', 0.5, { opacity: 0 })
            .call(this.setRound);
        }
      });
    },

    handleActivityEnd() {
      this.hideKeyboard();
      TweenMax.delayedCall(0.3, this.completeActivity);
    }
  },
  watch: {
    currentStep(next) {
      if (next && typeof next === 'string') {
        const instructions = `Type the word - ${next}`;
        this.setScreenReaderAlert(this.getTouchHoldBasedInstructions(instructions));
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/fade.scss';

.words-container {
  padding: 45px;
  position: relative;
  height: 100%;
  font-size: 3em;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  p {
    display: inline-block;
    font-size: 1em;
    margin-right: 50px;
  }
}

.tablet {
  .activity-stage {
    align-items: start;
  }
}
</style>
